import {useCallback, useEffect, useRef, useState} from 'react';
import {normalizeBudgetReference} from './goalBudgetEstimates';

export default function useGoalBudgetReference() {
  const [state, setState] = useState({reference: null, loading: true, now: Date.now()});
  const refreshRef = useRef(null);
  const refresh = useCallback(() => refreshRef.current?.(), []);
  useEffect(() => {
    let stopped = false;
    let active = null;
    let lastAttempt = -Infinity;
    const fetchReference = async () => {
      if (stopped || active) return;
      const controller = new AbortController();
      active = controller;
      lastAttempt = Date.now();
      setState(current => ({...current, loading: true, now: Date.now()}));
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch('/api/public/goal-budget-reference', {credentials: 'omit', cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('reference-unavailable');
        const reference = normalizeBudgetReference(await response.json());
        if (!reference) throw new Error('invalid-reference');
        if (!stopped) setState({reference, loading: false, now: Date.now()});
      } catch (_) {
        if (!stopped) setState(current => ({...current, loading: false, now: Date.now(),
          reference: current.reference ? {...current.reference, connection: 'retained'} : null}));
      } finally {clearTimeout(timer); active = null;}
    };
    const resume = () => {if (document.visibilityState !== 'hidden' && Date.now() - lastAttempt >= 15000) fetchReference();};
    refreshRef.current = fetchReference;
    fetchReference();
    const interval = setInterval(() => {if (document.visibilityState !== 'hidden') fetchReference();}, 60000);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {stopped = true; refreshRef.current = null; clearInterval(interval); active?.abort();
      document.removeEventListener('visibilitychange', resume); window.removeEventListener('focus', resume);};
  }, []);
  return {...state, refresh};
}
